import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileSpreadsheet, FileText, Edit, Trash2, Briefcase, ChevronDown, ChevronRight, Package } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { usePermission } from "@/hooks/usePermission";

const ESTATUS_OPTS = ["Pendiente", "En proceso", "Terminado", "Entregado", "Cancelado"];

function InfoRow({ label, value, mono = false }) {
  return (
    <div className="flex flex-col gap-0.5 p-3 rounded-md bg-muted/30 border border-border">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={`text-sm ${mono ? "font-mono" : ""} text-foreground`}>{value || "-"}</span>
    </div>
  );
}

function fmtMoney(v) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
}

function BundleDetailTable({ items }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (idx) => setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));

  return (
    <div className="divide-y divide-border">
      {items.map((it, idx) => {
        const isOpen = expanded[idx] !== false;
        const comps = it.componentes || [];
        return (
          <div key={idx} className="p-3" data-testid={`otac-detail-bundle-${idx}`}>
            {/* Bundle header */}
            <div className="flex items-center gap-3">
              <button type="button" className="shrink-0" onClick={() => toggle(idx)}>
                {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              </button>
              <Badge variant="secondary" className="shrink-0 text-[10px]">
                <Package className="h-3 w-3 mr-1" /> Bundle {idx + 1}
              </Badge>
              <div className="grid grid-cols-7 gap-4 flex-1 text-sm">
                <div><span className="text-[10px] uppercase text-muted-foreground block">Clave Bundle</span><span className="font-mono font-medium">{it.sku || "-"}</span></div>
                <div><span className="text-[10px] uppercase text-muted-foreground block">VPN</span><span>{it.vpn || "-"}</span></div>
                <div><span className="text-[10px] uppercase text-muted-foreground block">Descripción</span><span>{it.descripcion || "-"}</span></div>
                <div><span className="text-[10px] uppercase text-muted-foreground block">UPC</span><span className="font-mono">{it.upc || "-"}</span></div>
                <div><span className="text-[10px] uppercase text-muted-foreground block">QTY</span><span className="font-mono tabular-nums">{it.qty || 0}</span></div>
                <div><span className="text-[10px] uppercase text-muted-foreground block">Costo/Pza</span><span className="font-mono tabular-nums">{fmtMoney(it.costo_pieza)}</span></div>
                <div><span className="text-[10px] uppercase text-muted-foreground block">Total</span><span className="font-mono tabular-nums font-semibold text-primary">{fmtMoney(it.total_partida)}</span></div>
              </div>
            </div>
            {/* Components sub-table */}
            {isOpen && comps.length > 0 && (
              <div className="ml-10 mt-2 bg-muted/20 rounded-md border border-border/50 p-2">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-1 block">Componentes ({comps.length})</span>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                      <th className="py-1 px-2 w-8">#</th>
                      <th className="py-1 px-2">SKU</th>
                      <th className="py-1 px-2">VPN</th>
                      <th className="py-1 px-2">UPC</th>
                      <th className="py-1 px-2 text-right">Piezas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comps.map((c, ci) => (
                      <tr key={ci} className="border-t border-border/40">
                        <td className="py-1.5 px-2 text-muted-foreground font-mono">{ci + 1}</td>
                        <td className="py-1.5 px-2 font-mono font-medium">{c.sku || "-"}</td>
                        <td className="py-1.5 px-2">{c.vpn || "-"}</td>
                        <td className="py-1.5 px-2 font-mono">{c.upc || "-"}</td>
                        <td className="py-1.5 px-2 text-right font-mono tabular-nums font-medium">{c.qty || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function OTACDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canEdit = usePermission("otac_edit");
  const canExport = usePermission("otac_export");
  const canDelete = usePermission("otac_delete");

  const { data: ot, isLoading } = useQuery({
    queryKey: ["otac", id],
    queryFn: async () => (await api.get(`/ots-comercial/${id}`)).data,
  });

  const downloadFile = async (kind) => {
    try {
      const res = await api.get(`/ots-comercial/${id}/${kind}`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OT_AC_${ot.folio}.${kind === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} descargado`);
    } catch (e) {
      toast.error("Error al descargar");
    }
  };

  const changeStatus = async (newStatus) => {
    try {
      await api.patch(`/ots-comercial/${id}`, { estatus: newStatus });
      toast.success("Estatus actualizado");
      qc.invalidateQueries({ queryKey: ["otac", id] });
      qc.invalidateQueries({ queryKey: ["otac-list"] });
    } catch (e) {
      toast.error("Error al actualizar estatus");
    }
  };

  const deleteOT = async () => {
    try {
      await api.delete(`/ots-comercial/${id}`);
      toast.success("OT Comercial eliminada");
      navigate("/ots-comercial");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  if (isLoading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full" />)}</div>;
  if (!ot) return <div className="text-center py-12 text-muted-foreground">OT Comercial no encontrada</div>;

  return (
    <div className="space-y-4" data-testid="otac-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button size="icon" variant="ghost" onClick={() => navigate("/ots-comercial")} data-testid="otac-detail-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-semibold tracking-tight font-mono" data-testid="otac-detail-folio">{ot.folio}</h1>
              <StatusBadge status={ot.estatus} />
            </div>
            <p className="text-sm text-muted-foreground">{ot.cliente} - {ot.solicitante}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && (
            <Select value={ot.estatus || "Pendiente"} onValueChange={changeStatus}>
              <SelectTrigger className="w-40" data-testid="otac-detail-estatus-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ESTATUS_OPTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {canExport && (
            <>
              <Button size="sm" variant="outline" onClick={() => downloadFile("excel")} data-testid="otac-detail-excel">
                <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadFile("pdf")} data-testid="otac-detail-pdf">
                <FileText className="h-4 w-4 mr-1" /> PDF
              </Button>
            </>
          )}
          {canEdit && (
            <Button size="sm" asChild data-testid="otac-detail-edit">
              <Link to={`/ots-comercial/${id}/edit`}><Edit className="h-4 w-4 mr-1" /> Editar</Link>
            </Button>
          )}
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="icon" variant="destructive" className="h-9 w-9" data-testid="otac-detail-delete">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminar {ot.folio}?</AlertDialogTitle>
                  <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteOT} data-testid="otac-detail-delete-confirm">Eliminar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <InfoRow label="Tipo de OT" value={ot.tipo_ot || "-"} />
        <InfoRow label="Folio Comercial" value={ot.folio_comercial || "-"} />
        <InfoRow label="Cliente" value={ot.cliente} />
        <InfoRow label="Solicitante" value={ot.solicitante} />
        <InfoRow label="Fecha Compromiso" value={ot.fecha_compromiso} mono />
        <InfoRow label="Forma de Pago" value={ot.forma_pago} />
        <InfoRow label={"Área"} value={ot.area} />
        <InfoRow label="Tipo de Trabajo" value={ot.tipo_trabajo} />
        <InfoRow label="Requerimientos de OT" value={(ot.requerimientos_ot || []).join(", ") || ot.tipo_etiquetado || "-"} />
        <InfoRow label={"Aut. Comercialización"} value={ot.autorizacion_comercializacion} />
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <Card className="bg-card border-border">
          <CardContent className="p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total QTY</div>
            <div className="text-lg font-mono font-bold tabular-nums" data-testid="otac-detail-total-qty">{ot.total_qty || 0}</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Partidas</div>
            <div className="text-lg font-mono font-bold tabular-nums text-primary" data-testid="otac-detail-total-costo">{fmtMoney(ot.total_costo)}</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">SKUs / Partidas</div>
            <div className="text-lg font-mono font-bold tabular-nums">{ot.unique_skus || 0} / {(ot.items || []).length}</div>
          </CardContent>
        </Card>
      </div>
      {/* Costos Maquila / Comercial */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Card className="bg-card border-border border-l-4 border-l-amber-500/60">
          <CardContent className="p-3 flex justify-between items-center">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Costo Maquila</div>
              <div className="text-xs text-muted-foreground">Total de partidas</div>
            </div>
            <div className="text-lg font-mono font-bold tabular-nums text-amber-400" data-testid="otac-detail-costo-maquila">{fmtMoney(ot.costo_maquila)}</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border border-l-4 border-l-emerald-500/60">
          <CardContent className="p-3 flex justify-between items-center">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Costo Comercial</div>
              <div className="text-xs text-muted-foreground">Lo que cobras al área comercial</div>
            </div>
            <div className="text-lg font-mono font-bold tabular-nums text-emerald-400" data-testid="otac-detail-costo-comercial">{fmtMoney(ot.costo_comercial)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Items table */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Partidas {(ot.tipo_ot || '').toLowerCase() === "bundle" ? "(Bundles)" : `(${ot.tipo_ot || "Partidas"})`}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            {/* ─── CAMBIO DE CLAVE VIEW ─── */}
            {(ot.tipo_ot || '').toLowerCase() !== "bundle" && (
              <table className="w-full text-sm min-w-[800px]">
                <thead className="bg-muted/40">
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2.5 px-3 w-8">#</th>
                    <th className="py-2.5 px-3">SKU</th>
                    <th className="py-2.5 px-3">VPN</th>
                    <th className="py-2.5 px-3">Descripción</th>
                    <th className="py-2.5 px-3">Clave Vieja</th>
                    <th className="py-2.5 px-3">Clave Nueva</th>
                    <th className="py-2.5 px-3">UPC</th>
                    <th className="py-2.5 px-3 text-right">QTY</th>
                    <th className="py-2.5 px-3 text-right">Costo/Pza</th>
                    <th className="py-2.5 px-3 text-right">Total</th>
                    <th className="py-2.5 px-3">Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {(ot.items || []).map((it, idx) => (
                    <tr key={idx} className="border-t border-border/60 hover:bg-muted/30" data-testid={`otac-detail-item-${idx}`}>
                      <td className="py-2.5 px-3 text-muted-foreground font-mono">{idx + 1}</td>
                      <td className="py-2.5 px-3 font-mono font-medium">{it.sku || "-"}</td>
                      <td className="py-2.5 px-3">{it.vpn || "-"}</td>
                      <td className="py-2.5 px-3">{it.descripcion || "-"}</td>
                      <td className="py-2.5 px-3 font-mono">{it.clave_vieja || it.clave_final || "-"}</td>
                      <td className="py-2.5 px-3 font-mono">{it.clave_nueva || "-"}</td>
                      <td className="py-2.5 px-3 font-mono">{it.upc || "-"}</td>
                      <td className="py-2.5 px-3 text-right font-mono tabular-nums">{it.qty || 0}</td>
                      <td className="py-2.5 px-3 text-right font-mono tabular-nums">{fmtMoney(it.costo_pieza)}</td>
                      <td className="py-2.5 px-3 text-right font-mono tabular-nums font-semibold text-primary">{fmtMoney(it.total_partida)}</td>
                      <td className="py-2.5 px-3 text-muted-foreground text-xs">{it.observaciones || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* ─── BUNDLE VIEW ─── */}
            {(ot.tipo_ot || '').toLowerCase() === "bundle" && (
              <BundleDetailTable items={ot.items || []} />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Observaciones generales */}
      {ot.observaciones_generales && (
        <Card className="bg-card border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Observaciones Generales</CardTitle></CardHeader>
          <CardContent><p className="text-sm text-foreground/80">{ot.observaciones_generales}</p></CardContent>
        </Card>
      )}
    </div>
  );
}
